import { useCallback, useEffect, useRef, useState } from 'react';
import { sparkToast } from '@spark';
import {
    cancelScheduledUpgrade,
    cancelUpgrade,
    fetchBusy,
    fetchMe,
    fetchStatus,
    markNotificationsSeen,
    markWhatsNewSeen,
    scheduleUpgrade,
    snoozeOffer,
    startUpgrade,
    statusStreamUrl,
} from '../api/upgraderApi';
import { PLATFORM_LOCKED_EVENT } from '@/shared/http/client';
import { loadedPlatformVersion } from '@/shared/utils/platformVersion';
import { frontendServes, reloadedFor, rememberReloadFor } from '../utils/platformVersion';

const POLL_INTERVAL_MS = 60_000;
/** While an update runs without its live stream, so the progress moves and read-only mode ends promptly. */
const RUNNING_POLL_INTERVAL_MS = 5_000;
/** While the stream follows an update; polling then only keeps What's new and the reload check going. */
const STREAMING_POLL_INTERVAL_MS = 30_000;
/** After the stream was refused, e.g. by a gateway while the upgrader's or the frontend's pod is replaced. */
const STREAM_RETRY_MS = 3_000;
/**
 * The upgrader pings at least every 10 s. A connection to a pod that went away goes silent instead of
 * closing, so a stream this quiet is reconnected.
 */
const STREAM_SILENCE_MS = 25_000;
const FAILURE_SEEN_KEY = 'platform-upgrader:failure-seen';
/**
 * How long the frontend must have served the new version before the page reloads.
 * During a rolling update the old pods keep answering for several seconds after the
 * new ones do, and a reload then can load the old frontend or end on a 502 page.
 */
const ROLLOUT_SETTLE_MS = 45_000;
const SUPPORT_EMAIL = 'mailto:support@specific.ai';

/** The failed update this tab's Admin already closed, so it doesn't pop up again on every poll. */
function failureSeen() {
    try {
        return window.sessionStorage.getItem(FAILURE_SEEN_KEY);
    } catch {
        return null;
    }
}

function rememberFailureSeen(at) {
    try {
        window.sessionStorage.setItem(FAILURE_SEEN_KEY, at);
    } catch {
        // Without storage the popup only stays closed until this page reloads.
    }
}

/**
 * Which popup shows, in priority order. A closed popup stays closed until the
 * upgrader says otherwise (a day later, a newer offer, or a changed schedule).
 * None shows while an update runs; the in-progress card does instead.
 */
function popupKind({ status, me, error, failure, reopened }) {
    if (error || failure) return 'error';
    if (status?.upgrade) return null;
    if (!status?.update_available) return null;
    if (me?.snoozed && !reopened) return null;
    return status.schedule ? 'scheduled' : 'available';
}

const releasesKey = (releases) => releases.map((release) => release.version).join(',');

/**
 * Everything the update UI shows, polled from the platform-upgrader.
 *
 * ``enabled`` is false until the user may read status (signed in, with a role),
 * so the sign-in page never polls. A 404 means the upgrader is not installed:
 * polling stops and nothing renders.
 *
 * Once the platform runs a newer version than this page was loaded with, and the
 * frontend has served it for ``ROLLOUT_SETTLE_MS``, the page reloads itself to pick
 * up the new frontend, and What's new opens. A tab reloads at most once per version.
 *
 * An update first waits for running work (phase ``draining``), and the platform is
 * read-only from then on. A request the backend refuses with 423 polls at once, so
 * read-only mode does not wait for the next poll. While the update runs, its status
 * streams live; the stream reconnects while the upgrader and the frontend replace
 * their own pods. Admins see an update that failed until they close it.
 */
export default function usePlatformUpgrader({
    enabled,
    isAdmin,
    pollIntervalMs = POLL_INTERVAL_MS,
    reloadPage = () => window.location.reload(),
}) {
    const [installed, setInstalled] = useState(true);
    const [status, setStatus] = useState(null);
    const [me, setMe] = useState(null);
    const [reopened, setReopened] = useState(false);
    const [error, setError] = useState(null);
    const [modal, setModal] = useState(null);
    const [panelOpen, setPanelOpen] = useState(false);
    const [whatsNew, setWhatsNew] = useState([]);
    const [dismissedWhatsNew, setDismissedWhatsNew] = useState('');
    const [busy, setBusy] = useState(null);
    const [streaming, setStreaming] = useState(false);
    const [closedFailure, setClosedFailure] = useState(failureSeen);
    const busyRequest = useRef(0);
    const loadedVersion = useRef(loadedPlatformVersion());
    const servedSince = useRef(null);
    const reloading = useRef(false);
    // A ref, so a new reloadPage on every render does not change refresh and restart polling on every render.
    const reloadPageRef = useRef(reloadPage);
    useEffect(() => {
        reloadPageRef.current = reloadPage;
    }, [reloadPage]);

    const reloadOnceSettled = useCallback(async (running) => {
        if (!(await frontendServes(running))) {
            servedSince.current = null;
        } else if (servedSince.current?.version !== running) {
            servedSince.current = { version: running, at: Date.now() };
        } else if (Date.now() - servedSince.current.at >= ROLLOUT_SETTLE_MS && reloadedFor() !== running) {
            rememberReloadFor(running);
            reloading.current = true;
            reloadPageRef.current();
        }
    }, []);

    /** A status from a poll or the stream. */
    const applyStatus = useCallback(
        async (nextStatus) => {
            setStatus(nextStatus);
            const running = nextStatus.running_version;
            if (!loadedVersion.current) {
                loadedVersion.current = running;
            } else if (running !== loadedVersion.current && !reloading.current) {
                await reloadOnceSettled(running);
            }
        },
        [reloadOnceSettled]
    );

    const refresh = useCallback(async () => {
        try {
            const [nextStatus, nextMe] = await Promise.all([fetchStatus(), fetchMe()]);
            setMe(nextMe);
            await applyStatus(nextStatus);
        } catch (err) {
            if (err.status === 404) setInstalled(false);
        }
    }, [applyStatus]);

    const upgradeRunning = Boolean(status?.upgrade);
    const runningInterval = streaming ? STREAMING_POLL_INTERVAL_MS : RUNNING_POLL_INTERVAL_MS;
    const interval = upgradeRunning ? Math.min(pollIntervalMs, runningInterval) : pollIntervalMs;

    useEffect(() => {
        if (!enabled || !installed) return undefined;
        refresh();
        const id = setInterval(refresh, interval);
        return () => clearInterval(id);
    }, [enabled, installed, refresh, interval]);

    // The browser reconnects a stream that dropped by itself, but gives up on one that was refused, and waits
    // forever on one that went silent.
    useEffect(() => {
        if (!enabled || !installed || !upgradeRunning || typeof EventSource === 'undefined') return undefined;
        let source = null;
        let retry = null;
        let silence = null;
        let stopped = false;
        const reconnect = (delay) => {
            clearTimeout(silence);
            source.close();
            setStreaming(false);
            if (!stopped) retry = setTimeout(connect, delay);
        };
        const heard = () => {
            clearTimeout(silence);
            silence = setTimeout(() => reconnect(0), STREAM_SILENCE_MS);
        };
        function connect() {
            source = new EventSource(statusStreamUrl(), { withCredentials: true });
            heard();
            source.onopen = () => {
                heard();
                setStreaming(true);
            };
            source.addEventListener('ping', heard);
            source.onmessage = (event) => {
                heard();
                let next;
                try {
                    next = JSON.parse(event.data);
                } catch {
                    return;
                }
                applyStatus(next);
            };
            source.onerror = () => {
                setStreaming(false);
                if (source.readyState === EventSource.CLOSED) reconnect(STREAM_RETRY_MS);
            };
        }
        connect();
        return () => {
            stopped = true;
            clearTimeout(retry);
            clearTimeout(silence);
            source.close();
            setStreaming(false);
        };
    }, [enabled, installed, upgradeRunning, applyStatus]);

    // The stream carries only the status: once the update ends, read What's new and the rest at once.
    const wasRunning = useRef(false);
    useEffect(() => {
        if (wasRunning.current && !upgradeRunning) refresh();
        wasRunning.current = upgradeRunning;
    }, [upgradeRunning, refresh]);

    useEffect(() => {
        if (!enabled || !installed) return undefined;
        window.addEventListener(PLATFORM_LOCKED_EVENT, refresh);
        return () => window.removeEventListener(PLATFORM_LOCKED_EVENT, refresh);
    }, [enabled, installed, refresh]);

    const active = enabled && installed && status !== null;
    const failure =
        active && isAdmin && status.failure && status.failure.at !== closedFailure ? status.failure : null;
    const kind = active ? popupKind({ status, me, error, failure, reopened }) : null;
    const offer = active && status.update_available ? status.target : null;
    const upgrade = active ? status.upgrade || null : null;
    const draining = upgrade?.phase === 'draining';
    const inAppUpgrade = Boolean(status?.in_app_upgrade);
    const lastUpgrade = active && status.installed?.previous_version ? status.installed : null;
    const bellUnseen = Boolean(offer) && !me?.notifications_seen;

    // Until the page has reloaded onto the new version, What's new would describe a frontend it isn't running.
    const pageIsCurrent = active && status.running_version === loadedVersion.current;
    const unseen = pageIsCurrent && me?.whats_new?.length ? me.whats_new : null;
    const unseenKey = unseen ? releasesKey(unseen) : '';

    useEffect(() => {
        if (unseen && !modal && unseenKey !== dismissedWhatsNew) {
            setPanelOpen(false);
            setWhatsNew(unseen);
            setModal('whats-new');
        }
    }, [unseen, unseenKey, modal, dismissedWhatsNew]);

    // What an update waits for stops mattering once it starts, or is cancelled by another Admin.
    useEffect(() => {
        if (modal === 'waiting' && !draining) setModal(null);
    }, [modal, draining]);

    /** The work an update would wait for, read afresh whenever a modal shows it; a newer read wins. */
    const loadBusy = useCallback(async () => {
        const requestId = ++busyRequest.current;
        setBusy({ loading: true });
        let next;
        try {
            next = await fetchBusy();
        } catch (err) {
            next = { failed: true, detail: err.detail };
        }
        if (requestId === busyRequest.current) setBusy(next);
    }, []);

    const snooze = useCallback(async () => {
        await snoozeOffer().catch(() => {});
        await refresh();
    }, [refresh]);

    const dismissError = useCallback(() => {
        setError(null);
        if (failure) {
            rememberFailureSeen(failure.at);
            setClosedFailure(failure.at);
        }
    }, [failure]);

    const closePopup = useCallback(async () => {
        setReopened(false);
        if (kind === 'error') {
            dismissError();
        } else if (kind) {
            await snooze();
        }
    }, [kind, snooze, dismissError]);

    const openModal = useCallback((name) => {
        setPanelOpen(false);
        setModal(name);
    }, []);

    /** Opening the panel clears the bell's count for this user, until a newer offer or a changed schedule. */
    const togglePanel = useCallback(() => {
        if (!panelOpen && bellUnseen) {
            setMe((current) => ({ ...current, notifications_seen: true }));
            markNotificationsSeen().catch(() => {});
        }
        setPanelOpen(!panelOpen);
    }, [panelOpen, bellUnseen]);

    /** What's new counts as seen only once the user closes it, so a reload before that shows it again. */
    const closeWhatsNew = useCallback(async () => {
        setDismissedWhatsNew(releasesKey(whatsNew));
        setModal(null);
        await markWhatsNewSeen().catch(() => {});
        await refresh();
    }, [whatsNew, refresh]);

    const startUpdateFlow = useCallback(() => {
        if (isAdmin && inAppUpgrade) {
            openModal('before');
            loadBusy();
        } else {
            setReopened(true);
        }
    }, [isAdmin, inAppUpgrade, openModal, loadBusy]);

    /** Without ``force`` the upgrader waits for running work first; with it, it starts now. */
    const startUpdate = useCallback(
        async (force) => {
            setModal(null);
            setReopened(false);
            try {
                await startUpgrade({ force });
                await refresh();
            } catch (err) {
                setError(err.detail);
            }
        },
        [refresh]
    );

    const showWaitingFor = useCallback(() => {
        openModal('waiting');
        loadBusy();
    }, [openModal, loadBusy]);

    const cancelUpdate = useCallback(async () => {
        setModal(null);
        try {
            await cancelUpgrade();
        } catch (err) {
            sparkToast.error(err.detail);
        }
        await refresh();
    }, [refresh]);

    const schedule = useCallback(
        async (at) => {
            try {
                await scheduleUpgrade(at.toISOString());
                setModal(null);
                setReopened(false);
                await refresh();
            } catch (err) {
                sparkToast.error(err.detail);
            }
        },
        [refresh]
    );

    /** The update goes back into the bell for the Admin who cancelled, without popping up again. */
    const cancelSchedule = useCallback(async () => {
        try {
            await cancelScheduledUpgrade();
            setReopened(false);
            await snooze();
        } catch (err) {
            sparkToast.error(err.detail);
        }
    }, [snooze]);

    const contactSupport = useCallback(() => {
        dismissError();
        window.open(SUPPORT_EMAIL);
    }, [dismissError]);

    return {
        active,
        isAdmin,
        inAppUpgrade,
        offer,
        offeredAt: status?.offered_at || null,
        schedule: status?.schedule || null,
        scheduleWindow: status?.schedule_window || null,
        lastUpgrade,
        upgrade,
        draining,
        readOnly: Boolean(upgrade),
        busy,
        error: error || failure?.reason || null,
        popup: modal || panelOpen ? null : kind,
        modal,
        panelOpen,
        whatsNew,
        bellVisible: active,
        bellCount: bellUnseen ? '1' : undefined,
        closePopup,
        togglePanel,
        closePanel: () => setPanelOpen(false),
        closeWhatsNew,
        startUpdateFlow,
        openSchedule: () => openModal('schedule'),
        closeModal: () => setModal(null),
        updateNow: () => startUpdate(false),
        updateAnyway: () => startUpdate(true),
        showWaitingFor,
        cancelUpdate,
        scheduleUpgrade: schedule,
        cancelSchedule,
        contactSupport,
    };
}
